import React, { SetStateAction, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  AccessPayload,
  AuthResponse,
  CommonModalPayload,
  LoginStage,
} from '../../../../common/types/interfaces'
import { minContacts, USER_ROUTE, SHOULD_COLLECT_EMAIL } from '../../../../common/utils/constants'
import { getApi } from '../../../../api/api'
import { setChatModalState, setChatScreen, setTopicList } from '../../../../store/slices/chatSlice'
import { AfterActions, ChatScreens, CommonModals, Group } from '../../../../common/types/enums'
import { useChatForOrders } from '../../../../common/hooks/useChatForOrders'
import { delayAction } from '../../../../common/utils/functions/delayAction'
import { jwtDecode } from 'jwt-decode'
import { setCommonModalState } from '../../../../store/slices/commonModalSlice'
import { selectCurrentUser } from '../../../../store/slices/currentUserSlice'

export const useTelegramLoginForm = (
  setModalActive:
    | React.Dispatch<SetStateAction<boolean>>
    | ((state: boolean) => {
        payload: CommonModalPayload
        type: 'common-modal/setCommonModalState'
      }),
  onAttachEmail?: () => void,
) => {
  const [stage, setStage] = useState<LoginStage>(LoginStage.botLink)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isLogin, setIsLogin] = useState(true)
  const [api] = useState(() => getApi())
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [legalityChecked, setLegalityChecked] = useState(false)
  const afterActionRoute = localStorage.getItem('afterActionRoute')
  const { openChatWindow } = useChatForOrders()
  const [searchParams, setSearchParams] = useSearchParams()
  const params = Object.fromEntries(searchParams.entries())

  const { id } = params || {}
  useEffect(() => {
    // setSearchParams({})
  }, [id])

  const currentUser = useSelector(selectCurrentUser)

  const setPaymentModalState = useCallback(
    (state: boolean) => dispatch(setCommonModalState({ modal: CommonModals.PAYMENT, state })),
    [dispatch],
  )

  const getTopicList = useCallback(
    async (userId: number) => {
      try {
        const result = await api.getTopicList(userId)
        if (result.length > 0) dispatch(setTopicList(result))
        return result
      } catch (error) {}
    },
    [api, dispatch],
  )

  const botLoginAction = useCallback(async () => {
    try {
      setLoading(true)
      setStage(LoginStage.botLoginProcess)
      setMessage('Идет авторизация, пожалуйста подождите')
      await delayAction(700)
      let result: AuthResponse
      if (isLogin) {
        result = await api.botLogin(params)
      } else {
        result = await api.botRegister(params)
      }
      if (result) {
        if (result.registerAction) {
          setMessage(
            'Мы не нашли ваш аккаунт в нашей системе. После нажатия кнопки будет выполнена регистрация. Пожалуйста, подтвердите согласие с документами ниже',
          )
          setIsLogin(false)
          setLoading(false)
          return
        } else if (result.dateNotValid) {
          setStage(LoginStage.botLoginError)
          setSearchParams({})
          setMessage('Данные устрели, перейдите в бот, для повторной авторизации')
          setLoading(false)
        } else if (result.accessToken) {
          const payload: AccessPayload = jwtDecode(result.accessToken)

          const afterAction = localStorage.getItem('afterAction')
     
          setSearchParams({})
          setStage(LoginStage.botLoginSuccess)
          dispatch(setChatModalState(false))
          dispatch(setChatScreen(ChatScreens.LIST))
          setMessage(isLogin ? 'Вы авторизованы' : 'Вы зарегистрированы')
          setLoading(false)
          await delayAction(2000)

          // 1. Сначала проверяем afterActionRoute
          if (afterActionRoute) {
            setModalActive(false)
            localStorage.removeItem('afterActionRoute')
            navigate(afterActionRoute)
            return
          }

          // 2. Потом проверяем afterAction для чата
          if (afterAction && JSON.parse(afterAction).action === AfterActions.START_CHAT) {
            setModalActive(false)
            const topicsList = await getTopicList(payload.userId)
            const field =
              JSON.parse(afterAction).group === Group.CARGO
                ? 'cargoId'
                : JSON.parse(afterAction).group === Group.TRANSPORT
                  ? 'transportId'
                  : 'slotId'
            const weHaveIt = topicsList?.find(
              (el) =>
                (el.userId === payload.userId || el.participantId === payload.userId) &&
                el[field] === JSON.parse(afterAction).order.id,
            )
            if (payload.contacts < minContacts && !weHaveIt) {
              setPaymentModalState(true)
              return
            }
            await openChatWindow(
              JSON.parse(afterAction).order,
              JSON.parse(afterAction).order.group,
              payload.userId,
            )
            localStorage.removeItem('afterAction')
            return
          }

          // Проверяем наличие email через токен
          const decoded = jwtDecode<AccessPayload>(result.accessToken)
          const hasEmail = !!decoded.email

          const shouldCollectEmail = SHOULD_COLLECT_EMAIL && !hasEmail

          if (shouldCollectEmail && onAttachEmail) {
            onAttachEmail()
            return
          }

          // 4. Нет afterAction, нет сбора email - на главную
          setModalActive(false)
          navigate(USER_ROUTE)
        }
      }
    } catch (error: any) {
      if (error.response?.status && error.response.status === 401) {
        setMessage('Возникла ошибка авторизации, вернитесь в бот, и повторите вход')
      } else {
        setMessage('Возникла ошибка авторизации обратитесь в поддержку')
      }
      setStage(LoginStage.botLoginError)
      setLoading(false)
      await delayAction(3000)
      setModalActive(false)
    }
  }, [
    api,
    dispatch,
    isLogin,
    navigate,
    openChatWindow,
    afterActionRoute,
    setModalActive,
    setPaymentModalState,
    getTopicList,
    params,
    setSearchParams,
    onAttachEmail,
  ])

  const toggleLegality = useCallback(() => {
    setLegalityChecked((prev) => !prev)
  }, [])

  useEffect(() => {
    return () => {
      afterActionRoute && localStorage.removeItem('afterActionRoute')
    }
  }, [])

  useEffect(() => {
    if (id && !currentUser) botLoginAction()
  }, [id])

  return {
    stage,
    loading,
    message,
    isLogin,
    toggleLegality,
    legalityChecked,
    botLoginAction,
  }
}
